<template>
    <div class="flex flex-col items-center">
        <div class="relative" :class="sizeClass">
            <!-- Simple SVG Donut Chart -->
            <svg viewBox="0 0 100 100" class="w-full h-full transform -rotate-90">
                <circle v-for="(segment, index) in chartSegments" :key="index" cx="50" cy="50" r="40" fill="transparent"
                    stroke-width="12" :stroke="segment.color" :stroke-dasharray="segment.dashArray"
                    :stroke-dashoffset="segment.dashOffset" class="transition-all duration-300 ease-out hover-circle"
                    :class="{ 'opacity-40': currentHoveredId && currentHoveredId !== segment.id, 'stroke-[16]': currentHoveredId === segment.id }"
                    @mouseenter="currentHoveredId = segment.id" @mouseleave="currentHoveredId = null">
                    <title>{{ segment.name }}: ${{ segment.total }}</title>
                </circle>
                <!-- Center White Circle for Donut Effect -->
                <circle cx="50" cy="50" r="30" fill="white" />
            </svg>

            <!-- Center Total Text -->
            <!-- Center Total Text / Dynamic Info -->
            <div
                class="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none transition-all duration-300">
                <template v-if="hoveredCategory">
                    <span class="text-[9px] text-rose-500 uppercase font-black tracking-widest mb-1">{{
                        hoveredCategory.category_name }}</span>
                    <span class="text-xl font-black text-slate-900 leading-none">${{ hoveredCategory.total }}</span>
                    <span class="text-[10px] font-bold text-slate-400 mt-1">{{ Math.round((hoveredCategory.total /
                        total) * 100) }}% del total</span>
                </template>
                <template v-else>
                    <span class="text-[10px] text-slate-400 uppercase font-bold tracking-tighter">Gasto Total</span>
                    <span class="font-black text-slate-900 leading-none" :class="totalTextClass">${{ total }}</span>
                    <div v-if="topCategory" class="mt-1 flex flex-col items-center">
                        <span class="text-[8px] text-slate-400 uppercase font-medium">Top: {{ topCategory.category_name
                        }}</span>
                        <div class="h-0.5 w-8 rounded-full bg-slate-100 mt-0.5"></div>
                    </div>
                    <span v-else class="text-[10px] font-bold text-emerald-500 mt-1">Este Mes</span>
                </template>
            </div>
        </div>

        <!-- Legend -->
        <div v-if="showLegend" class="mt-8 grid grid-cols-2 gap-x-4 gap-y-2 w-full">
            <div v-for="cat in categories.slice(0, 4)" :key="cat.category_id"
                class="flex items-center space-x-2 cursor-default transition-opacity duration-300"
                :class="{ 'opacity-30': currentHoveredId && currentHoveredId !== cat.category_id }"
                @mouseenter="currentHoveredId = cat.category_id" @mouseleave="currentHoveredId = null">
                <div class="w-3 h-3 rounded-full transition-transform duration-300"
                    :class="{ 'scale-125 shadow-sm': currentHoveredId === cat.category_id }"
                    :style="{ backgroundColor: cat.color }">
                </div>
                <span class="text-xs text-slate-600 truncate max-w-[100px]"
                    :class="{ 'font-bold text-slate-900': currentHoveredId === cat.category_id }">{{ cat.category_name
                    }}</span>
                <span class="text-[10px] font-bold text-slate-400">{{ Math.round((cat.total / total) * 100) }}%</span>
            </div>
            <div v-if="categories.length > 4"
                class="col-span-2 text-center text-[10px] text-slate-400 mt-1 uppercase tracking-widest font-bold">
                + {{ categories.length - 4 }} categorías más
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, defineEmits } from 'vue'

const props = defineProps({
    categories: {
        type: Array,
        required: true
    },
    total: {
        type: [Number, String],
        required: true
    },
    size: {
        type: String,
        default: 'md' // sm, md, lg
    },
    showLegend: {
        type: Boolean,
        default: true
    },
    modelValue: {
        type: [Number, String, null],
        default: null
    }
})

const emit = defineEmits(['update:modelValue'])

const internalHoveredId = ref(null)
const currentHoveredId = computed({
    get: () => props.modelValue !== undefined && props.modelValue !== null ? props.modelValue : internalHoveredId.value,
    set: (val) => {
        internalHoveredId.value = val
        emit('update:modelValue', val)
    }
})

const hoveredCategory = computed(() => {
    if (!currentHoveredId.value) return null
    return props.categories.find(c => c.category_id === currentHoveredId.value) || null
})

const topCategory = computed(() => {
    if (!props.categories.length) return null
    return [...props.categories].sort((a, b) => b.total - a.total)[0]
})

const sizeClass = computed(() => {
    return {
        'w-32 h-32': props.size === 'sm',
        'w-64 h-64': props.size === 'md',
        'w-80 h-80': props.size === 'lg'
    }
})

const totalTextClass = computed(() => {
    return {
        'text-sm': props.size === 'sm',
        'text-xl': props.size === 'md',
        'text-2xl': props.size === 'lg'
    }
})

// SVG Donut Chart Logic
const chartSegments = computed(() => {
    if (!props.categories.length || !props.total) return []

    const totalNum = parseFloat(props.total)
    if (totalNum <= 0) return []

    let currentOffset = 0
    const radius = 40
    const circumference = 2 * Math.PI * radius

    return props.categories.map(cat => {
        const percent = cat.total / totalNum
        const dashLength = circumference * percent

        const segment = {
            id: cat.category_id,
            name: cat.category_name,
            total: cat.total,
            color: cat.color,
            dashArray: `${dashLength} ${circumference}`,
            dashOffset: -currentOffset,
            isHovered: currentHoveredId.value === cat.category_id
        }

        currentOffset += dashLength

        return segment
    })
})
</script>

<style scoped>
svg circle {
    transition: stroke-dasharray 0.8s ease-in-out, stroke-dashoffset 0.8s ease-in-out, stroke-width 0.3s ease, opacity 0.3s ease;
}

.hover-circle {
    cursor: pointer;
}
</style>
